/**
 * HX Ecosystems Operations Center — Phase 1 shell.
 * Second-brain platform: dashboard, suppliers, projects, documents, global search.
 * Route: /ops (also served on /mou for backward compat via App.js redirect).
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Users, FolderKanban, FileText, Presentation, Video,
  Library, Sparkles, CalendarClock, Settings, Search, ArrowUpRight,
  ExternalLink, Mail, Phone, Globe, Download, ChevronRight, X, Home as HomeIcon,
} from "lucide-react";
import AiAssistantPanel from "./AiAssistantPanel";
import PresentationsSection from "./PresentationsSection";
import SupplierTabs from "./SupplierTabs";
import MeetingsSection from "./MeetingsSection";

const P = {
  bg:      "#0A0F1A",
  surface: "rgba(255,255,255,0.03)",
  border:  "rgba(255,255,255,0.08)",
  ink:     "#E4E7EB",
  dim:     "rgba(228,231,235,0.62)",
  accent:  "#3B82F6",  // Ops center = tech blue
  accent2: "#22C55E",
  silver:  "#C0C7CE",
};

// ------------------------------------------------------------ DATA

// Partner data comes from shared module
import { STRATEGIC_GROUPS, SUPPLIERS } from "@/data/partners";


const PROJECTS = [
  { id: "hannonexus",   name: "HannoNexus Energy",   status: "Active",   country: "Germany",  color: P.accent2, desc: "Uetze Consortium — Industry 4.0 blueprint integrating Siemens · VCOW · DFOPTIX.", link: "/neunexus" },
  { id: "neugreen",     name: "NeuGreen Energy",     status: "Active",   country: "Germany",  color: "#22C55E", desc: "Intelligent Energy Ecosystem for the German market — solar, storage, mobility, digital.", link: "/neugreen" },
  { id: "hx-flow",      name: "HX Flow AI",          status: "Planning", country: "EU",       color: "#F59E0B", desc: "AI-driven workflow automation platform for the ecosystem.", link: "#" },
  { id: "hx-smart",     name: "HX Smart Point",      status: "Active",   country: "EU · CV",  color: "#A855F7", desc: "Mobility experience infrastructure — smart charging + hospitality.", link: "#" },
  { id: "bsgotech",     name: "BSGOTECH",              status: "Parent",   country: "EU",       color: P.accent,  desc: "Parent technology company — AI · Cloud · Data · Innovation across the ecosystem.", link: "/" },
  { id: "mionga",       name: "MIONGA Beverages",    status: "Active",   country: "DE · CV",  color: "#D4A857", desc: "Premium lifestyle beverages ecosystem — Cape Verde to Germany.", link: "/mionga" },
  { id: "cabo-verde",   name: "BSGOTECH × Cabo Verde", status: "Initiative 2026", country: "Cabo Verde", color: "#C9A24A", desc: "Strategic market initiative — Eco Collection (Live), Nu Bai Mobility MVP, Nu Bai Pay concept, HX future roadmap. 6 institutional documents in the library.", link: "/cabo-verde" },
];

const DOCUMENTS = SUPPLIERS.flatMap((s) => s.documents.map((d) => ({ ...d, supplier: s.name, supplierId: s.id, category: s.category })));

const VIDEOS = [
  {
    id: "v1",
    title: "Bluesun Solar — Product Overview",
    supplier: "Bluesun Solar",
    category: "Product Demo",
    duration: "0:45",
    url: "https://customer-assets-cm19k8pv.emergentagent.net/job_hydro-protect-7/artifacts/uz9ojor6_WhatsApp%20Video%202026-07-16%20at%2012.42.55.mp4",
    date: "2026-07-16",
  },
];

const NAV = [
  { id: "dashboard",    label: "Dashboard",         icon: LayoutDashboard },
  { id: "suppliers",    label: "Suppliers",         icon: Users },
  { id: "projects",     label: "Projects",          icon: FolderKanban },
  { id: "documents",    label: "Documents",         icon: FileText },
  { id: "presentations",label: "Presentations",     icon: Presentation },
  { id: "videos",       label: "Videos",            icon: Video },
  { id: "library",      label: "Technical Library", icon: Library,      soon: true },
  { id: "ai",           label: "BS Assistente",     icon: Sparkles },
  { id: "meetings",     label: "Meetings",          icon: CalendarClock },
  { id: "settings",     label: "Settings",          icon: Settings,     soon: true },
];

// ------------------------------------------------------------ SHARED UI
const SectionHeader = ({ eyebrow, title, subtitle }) => (
  <div className="mb-8">
    <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.accent }}>{eyebrow}</div>
    <h1 className="mt-2 font-bold tracking-tight text-[32px] lg:text-[40px] leading-tight" style={{ color: "#fff" }}>{title}</h1>
    {subtitle && <p className="mt-3 text-[14px] max-w-2xl" style={{ color: P.dim }}>{subtitle}</p>}
  </div>
);

const Card = ({ children, className = "", ...rest }) => (
  <div className={`rounded-lg p-5 ${className}`} style={{ background: P.surface, border: `1px solid ${P.border}` }} {...rest}>
    {children}
  </div>
);

// ------------------------------------------------------------ DASHBOARD
const Dashboard = ({ onNavigate }) => (
  <div>
    <SectionHeader eyebrow="Operations Center" title="Ecosystem overview." subtitle="Central knowledge and operations dashboard for BSGOTECH, NeuNexus and all divisions, suppliers, projects and documents." />
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      {[
        { label: "Active Projects",   value: PROJECTS.filter((p) => p.status === "Active").length, sub: "of " + PROJECTS.length + " total" },
        { label: "Suppliers",         value: SUPPLIERS.length, sub: "vetted technology sources" },
        { label: "Documents",         value: DOCUMENTS.length, sub: "PDFs · certificates · catalogs" },
        { label: "Countries",         value: 5, sub: "DE · CV · CN · EU · STP" },
      ].map((k, i) => (
        <Card key={i}>
          <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: P.dim }}>{k.label}</div>
          <div className="mt-2 font-bold text-[32px]" style={{ color: "#fff" }}>{k.value}</div>
          <div className="text-[11px] mt-1" style={{ color: P.silver }}>{k.sub}</div>
        </Card>
      ))}
    </div>
    <div className="grid lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-2">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[11px] uppercase tracking-[0.24em] font-semibold" style={{ color: P.accent }}>Active Projects</div>
          <button onClick={() => onNavigate("projects")} className="text-[11px] uppercase tracking-[0.22em] flex items-center gap-1 hover:text-white transition" style={{ color: P.silver }}>
            View all <ChevronRight size={12} />
          </button>
        </div>
        <div className="space-y-2">
          {PROJECTS.slice(0, 4).map((p) => (
            <div key={p.id} className="flex items-center gap-3 p-3 rounded-md" style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${P.border}` }}>
              <div className="w-1.5 h-8 rounded-full" style={{ background: p.color }} />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold truncate" style={{ color: "#fff" }}>{p.name}</div>
                <div className="text-[11px] truncate" style={{ color: P.dim }}>{p.desc}</div>
              </div>
              <span className="text-[9px] uppercase tracking-[0.22em] px-2 py-1 rounded-full shrink-0" style={{ background: `${p.color}22`, color: p.color, border: `1px solid ${p.color}44` }}>{p.status}</span>
            </div>
          ))}
        </div>
      </Card>
      <div className="space-y-4">
        <Card>
          <div className="text-[11px] uppercase tracking-[0.24em] font-semibold mb-4" style={{ color: P.accent }}>Strategic Industrial Partners</div>
          <div className="space-y-4">
            {STRATEGIC_GROUPS.map((g) => {
              const items = SUPPLIERS.filter((s) => s.group === g.id);
              if (items.length === 0) return null;
              return (
                <div key={g.id}>
                  <div className="text-[9px] uppercase tracking-[0.24em] mb-2" style={{ color: g.accent }}>{g.label}</div>
                  <div className="space-y-1">
                    {items.map((s) => (
                      <button key={s.id} onClick={() => onNavigate("suppliers", s.id)} className="w-full flex items-center gap-3 p-2 rounded-md transition text-left hover:bg-white/[0.03]">
                        {s.logo ? (
                          <div className="w-8 h-8 rounded flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,0.94)" }}>
                            <img src={s.logo} alt={s.name} className="max-h-6 max-w-6 object-contain" />
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded flex items-center justify-center shrink-0 text-[12px] font-bold" style={{ background: `${g.accent}22`, color: g.accent }}>{s.name[0]}</div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-[12px] font-semibold truncate flex items-center gap-2" style={{ color: "#fff" }}>
                            {s.name}
                            {s.status && s.status.includes("Proposed") && (
                              <span className="text-[8px] uppercase tracking-wider px-1 py-0.5 rounded" style={{ background: `${P.amber}18`, color: P.amber, border: `1px solid ${P.amber}44` }}>Proposed</span>
                            )}
                          </div>
                          <div className="text-[10px] truncate" style={{ color: P.dim }}>{s.category}</div>
                        </div>
                        <ArrowUpRight size={12} style={{ color: P.silver }} />
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
        <Card>
          <div className="text-[11px] uppercase tracking-[0.24em] font-semibold mb-4" style={{ color: P.accent }}>Admin Contact</div>
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-md flex items-center justify-center font-bold text-[13px] shrink-0" style={{ background: `linear-gradient(135deg, ${P.accent} 0%, ${P.accent2} 100%)`, color: P.bg }}>HX</div>
            <div>
              <div className="text-[13px] font-semibold" style={{ color: "#fff" }}>BSGOTECH Admin</div>
              <div className="text-[10.5px] mt-0.5" style={{ color: P.silver }}>BSG Otech · Germany</div>
            </div>
          </div>
          <div className="space-y-2">
            <a href="mailto:admin@bsgotech.com" data-testid="admin-email"
              className="flex items-center gap-2.5 p-2.5 rounded-md transition hover:bg-white/[0.04]"
              style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${P.border}` }}>
              <Mail size={13} style={{ color: P.accent }} />
              <span className="text-[12px] truncate" style={{ color: P.ink }}>admin@bsgotech.com</span>
            </a>
            <a href="tel:+4917684450554" data-testid="admin-phone"
              className="flex items-center gap-2.5 p-2.5 rounded-md transition hover:bg-white/[0.04]"
              style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${P.border}` }}>
              <Phone size={13} style={{ color: P.accent }} />
              <span className="text-[12px]" style={{ color: P.ink }}>+49 176 84450554</span>
            </a>
            <a href="https://wa.me/4917684450554" target="_blank" rel="noopener noreferrer" data-testid="admin-whatsapp"
              className="flex items-center gap-2.5 p-2.5 rounded-md transition hover:bg-white/[0.04]"
              style={{ background: `${P.accent2}0d`, border: `1px solid ${P.accent2}44` }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill={P.accent2}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              <span className="text-[12px]" style={{ color: P.accent2 }}>WhatsApp</span>
            </a>
          </div>
        </Card>
      </div>
    </div>
  </div>
);

// ------------------------------------------------------------ SUPPLIERS
const SuppliersSection = ({ selected, onSelect }) => {
  const s = selected ? SUPPLIERS.find((x) => x.id === selected) : null;
  const byGroup = STRATEGIC_GROUPS.map((g) => ({
    ...g,
    suppliers: SUPPLIERS.filter((sp) => sp.group === g.id),
  })).filter((g) => g.suppliers.length > 0);

  return (
    <div>
      <SectionHeader eyebrow="Strategic Industrial Partners" title="Partner directory." subtitle="Vetted technology partners across the industrial innovation stack — from digitalisation to renewable infrastructure and applied research." />
      <div className="space-y-8">
        {byGroup.map((g) => (
          <div key={g.id} data-testid={`supplier-group-${g.id}`}>
            <div className="flex items-baseline justify-between mb-4">
              <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: g.accent }}>
                {g.label} <span className="opacity-60">· {g.suppliers.length}</span>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {g.suppliers.map((sp) => (
                <button key={sp.id} onClick={() => onSelect(sp.id)} className="text-left rounded-lg p-5 transition hover:border-white/20" style={{ background: P.surface, border: `1px solid ${P.border}` }} data-testid={`supplier-card-${sp.id}`}>
                  <div className="flex items-center gap-3 mb-3">
                    {sp.logo ? (
                      <div className="w-12 h-12 rounded flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,0.94)" }}>
                        <img src={sp.logo} alt={sp.name} className="max-h-8 max-w-9 object-contain" />
                      </div>
                    ) : (
                      <div className="w-12 h-12 rounded flex items-center justify-center shrink-0 font-bold text-[18px]" style={{ background: `${g.accent}22`, color: g.accent }}>{sp.name[0]}</div>
                    )}
                    <div className="min-w-0">
                      <div className="text-[14px] font-semibold" style={{ color: "#fff" }}>{sp.name}</div>
                      <div className="text-[10px] uppercase tracking-[0.22em] mt-1" style={{ color: g.accent }}>{sp.category}</div>
                    </div>
                  </div>
                  {sp.status && sp.status !== "Active Partner" && (
                    <div className="mb-2 inline-block text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 rounded" style={{ background: `${P.amber}18`, color: P.amber, border: `1px solid ${P.amber}44` }}>
                      {sp.status}
                    </div>
                  )}
                  <p className="text-[12px] leading-snug line-clamp-3" style={{ color: P.dim }}>{sp.description}</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {sp.tags.slice(0, 4).map((t) => (
                      <span key={t} className="text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded" style={{ background: "rgba(255,255,255,0.04)", color: P.silver, border: `1px solid ${P.border}` }}>{t}</span>
                    ))}
                  </div>
                  <div className="mt-3 text-[10px] flex items-center gap-2" style={{ color: P.dim }}>
                    <FileText size={11} /> {sp.documents.length} document{sp.documents.length !== 1 ? "s" : ""}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <AnimatePresence>
        {s && <SupplierDetail supplier={s} onClose={() => onSelect(null)} />}
      </AnimatePresence>
    </div>
  );
};

const SupplierDetail = ({ supplier: s, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[100] flex items-center justify-center p-4"
    style={{ background: "rgba(10,15,26,0.9)", backdropFilter: "blur(20px)" }}
    onClick={onClose}
    data-testid="supplier-detail-modal"
  >
    <motion.div
      initial={{ opacity: 0, y: 16, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 16, scale: 0.98 }}
      onClick={(e) => e.stopPropagation()}
      className="relative w-full max-w-4xl max-h-[88vh] overflow-auto rounded-lg"
      style={{ background: P.bg, border: `1px solid ${P.border}` }}
    >
      <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/[0.08]" style={{ color: P.silver }} data-testid="supplier-detail-close">
        <X size={16} />
      </button>
      <div className="p-8">
        <div className="flex items-center gap-4 mb-5">
          {s.logo ? (
            <div className="w-16 h-16 rounded flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,0.94)" }}>
              <img src={s.logo} alt={s.name} className="max-h-10 max-w-12 object-contain" />
            </div>
          ) : (
            <div className="w-16 h-16 rounded flex items-center justify-center shrink-0 font-bold text-[24px]" style={{ background: `${P.accent}22`, color: P.accent }}>{s.name[0]}</div>
          )}
          <div>
            <h2 className="font-bold text-[24px] tracking-tight" style={{ color: "#fff" }}>{s.name}</h2>
            <div className="text-[11px] uppercase tracking-[0.22em] mt-1" style={{ color: P.accent }}>{s.category} · {s.country}</div>
          </div>
        </div>
        <p className="text-[14px] leading-relaxed" style={{ color: P.ink }}>{s.description}</p>
        <div className="mt-6 grid md:grid-cols-2 gap-3">
          {s.contact.email && <ContactRow icon={Mail}  label="Email"    value={s.contact.email}   href={`mailto:${s.contact.email}`} />}
          {s.contact.phone && <ContactRow icon={Phone} label="Phone"    value={s.contact.phone}   href={`tel:${s.contact.phone.replace(/\s/g, "")}`} />}
          {s.contact.website && <ContactRow icon={Globe} label="Website" value={s.contact.website} href={s.contact.website} external />}
          {s.contact.name && <ContactRow icon={Users}  label="Contact"  value={s.contact.name} />}
        </div>
        {s.documents.length > 0 && (
          <div className="mt-8">
            <div className="text-[10px] uppercase tracking-[0.32em] font-semibold mb-3" style={{ color: P.accent }}>Documents · {s.documents.length}</div>
            <div className="space-y-2">
              {s.documents.map((d, i) => (
                <a key={i} href={d.url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-md transition hover:bg-white/[0.04]"
                  style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${P.border}` }}
                  data-testid={`document-${s.id}-${i}`}>
                  <div className="w-9 h-9 rounded flex items-center justify-center shrink-0" style={{ background: `${P.accent}18`, color: P.accent }}>
                    <FileText size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold truncate" style={{ color: "#fff" }}>{d.name}</div>
                    <div className="text-[11px]" style={{ color: P.dim }}>{d.type} · {d.size}</div>
                  </div>
                  <Download size={14} style={{ color: P.silver }} />
                </a>
              ))}
            </div>
          </div>
        )}
        <div className="mt-6 flex flex-wrap gap-2">
          {s.tags.map((t) => (
            <span key={t} className="text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded" style={{ background: `${P.accent}12`, color: P.accent, border: `1px solid ${P.accent}33` }}>{t}</span>
          ))}
        </div>
        <SupplierTabs supplier={s} />
      </div>
    </motion.div>
  </motion.div>
);

const ContactRow = ({ icon: Icon, label, value, href, external }) => {
  const body = (
    <div className="flex items-center gap-3 p-3 rounded-md" style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${P.border}` }}>
      <Icon size={14} style={{ color: P.accent }} />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: P.dim }}>{label}</div>
        <div className="text-[12.5px] mt-0.5 truncate" style={{ color: P.ink }}>{value}</div>
      </div>
      {external && <ExternalLink size={12} style={{ color: P.silver }} />}
    </div>
  );
  return href ? <a href={href} target={external ? "_blank" : undefined} rel="noopener noreferrer">{body}</a> : body;
};

// ------------------------------------------------------------ PROJECTS
const ProjectsSection = () => (
  <div>
    <SectionHeader eyebrow="Portfolio" title="Projects." subtitle="Every entity, blueprint and initiative running under BSGOTECH." />
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {PROJECTS.map((p) => {
        const Wrapper = p.link.startsWith("/") ? Link : "div";
        const wrapProps = p.link.startsWith("/") ? { to: p.link } : {};
        return (
          <Wrapper key={p.id} {...wrapProps} className="block rounded-lg p-5 transition hover:border-white/20"
            style={{ background: P.surface, border: `1px solid ${P.border}` }} data-testid={`project-card-${p.id}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="w-2 h-8 rounded-full" style={{ background: p.color }} />
              <span className="text-[9px] uppercase tracking-[0.22em] px-2 py-1 rounded-full" style={{ background: `${p.color}22`, color: p.color, border: `1px solid ${p.color}44` }}>{p.status}</span>
            </div>
            <div className="text-[16px] font-bold tracking-tight" style={{ color: "#fff" }}>{p.name}</div>
            <div className="text-[10px] uppercase tracking-[0.22em] mt-1" style={{ color: P.silver }}>{p.country}</div>
            <p className="mt-3 text-[12.5px] leading-snug" style={{ color: P.dim }}>{p.desc}</p>
            {p.link.startsWith("/") && (
              <div className="mt-3 text-[10px] uppercase tracking-[0.22em] flex items-center gap-1" style={{ color: p.color }}>
                Open <ArrowUpRight size={11} />
              </div>
            )}
          </Wrapper>
        );
      })}
    </div>
  </div>
);

// ------------------------------------------------------------ DOCUMENTS
const DocumentsSection = () => (
  <div>
    <SectionHeader eyebrow="Knowledge Base" title="Documents." subtitle="All catalogs, certifications, technical sheets and proposals — indexed by supplier and category." />
    <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${P.border}` }}>
      <div className="grid grid-cols-[1.5fr_1fr_0.7fr_0.6fr_60px] px-5 py-3 text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ background: "rgba(255,255,255,0.03)", color: P.silver }}>
        <div>Name</div><div>Supplier</div><div>Category</div><div>Size</div><div></div>
      </div>
      {DOCUMENTS.map((d, i) => (
        <a key={i} href={d.url} target="_blank" rel="noopener noreferrer" className="grid grid-cols-[1.5fr_1fr_0.7fr_0.6fr_60px] items-center px-5 py-4 transition hover:bg-white/[0.03] border-t" style={{ borderColor: P.border }} data-testid={`doc-row-${i}`}>
          <div className="flex items-center gap-3 min-w-0">
            <FileText size={14} style={{ color: P.accent }} />
            <span className="text-[13px] truncate" style={{ color: "#fff" }}>{d.name}</span>
          </div>
          <div className="text-[12px]" style={{ color: P.dim }}>{d.supplier}</div>
          <div className="text-[10.5px] uppercase tracking-[0.2em]" style={{ color: P.silver }}>{d.category.split(" · ")[0]}</div>
          <div className="text-[11.5px]" style={{ color: P.dim }}>{d.size}</div>
          <Download size={14} style={{ color: P.silver }} />
        </a>
      ))}
    </div>
  </div>
);

// ------------------------------------------------------------ VIDEOS
const VideosSection = () => {
  const [playing, setPlaying] = React.useState(null);
  return (
    <div>
      <SectionHeader eyebrow="Video Library" title="Videos." subtitle="Product demos, factory tours and installation walkthroughs — one click away from any meeting." />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {VIDEOS.map((v) => (
          <button key={v.id} onClick={() => setPlaying(v)} data-testid={`video-card-${v.id}`}
            className="rounded-lg overflow-hidden text-left transition hover:border-white/20"
            style={{ background: P.surface, border: `1px solid ${P.border}` }}>
            <div className="relative aspect-video overflow-hidden" style={{ background: "#000" }}>
              <video src={v.url} className="absolute inset-0 w-full h-full object-cover" muted playsInline preload="metadata" />
              <div className="absolute inset-0 flex items-center justify-center transition group-hover:bg-black/20">
                <div className="w-14 h-14 rounded-full flex items-center justify-center backdrop-blur-md"
                  style={{ background: `${P.accent}dd`, boxShadow: `0 8px 30px -6px ${P.accent}`, color: "#fff" }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M5 3l14 9-14 9V3z"/></svg>
                </div>
              </div>
              <div className="absolute bottom-2 right-2 text-[10px] px-2 py-0.5 rounded font-mono" style={{ background: "rgba(0,0,0,0.7)", color: "#fff" }}>{v.duration}</div>
            </div>
            <div className="p-4">
              <div className="text-[13.5px] font-semibold" style={{ color: "#fff" }}>{v.title}</div>
              <div className="mt-1 flex items-center gap-2 text-[11px]" style={{ color: P.dim }}>
                <span>{v.supplier}</span><span className="opacity-40">·</span><span>{v.category}</span>
              </div>
              <div className="mt-2 text-[9.5px] uppercase tracking-[0.22em] font-mono" style={{ color: P.silver }}>{v.date}</div>
            </div>
          </button>
        ))}
      </div>
      <AnimatePresence>
        {playing && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ background: "rgba(10,15,26,0.95)", backdropFilter: "blur(20px)" }}
            onClick={() => setPlaying(null)} data-testid="video-player-modal">
            <button onClick={() => setPlaying(null)} data-testid="video-player-close"
              className="absolute top-5 right-5 w-11 h-11 rounded-full flex items-center justify-center hover:bg-white/[0.08] z-[101]" style={{ color: P.silver }}>
              <X size={16} />
            </button>
            <motion.div initial={{ scale: 0.96 }} animate={{ scale: 1 }} exit={{ scale: 0.96 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-5xl rounded-lg overflow-hidden"
              style={{ background: "#000", border: `1px solid ${P.border}` }}>
              <video src={playing.url} className="w-full h-auto max-h-[80vh]" controls autoPlay data-testid="video-player-element" />
              <div className="p-4 border-t" style={{ borderColor: P.border }}>
                <div className="text-[14px] font-semibold" style={{ color: "#fff" }}>{playing.title}</div>
                <div className="mt-1 text-[11px]" style={{ color: P.dim }}>{playing.supplier} · {playing.category} · {playing.date}</div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ------------------------------------------------------------ COMING SOON
const ComingSoon = ({ title, description }) => (
  <div>
    <SectionHeader eyebrow="Coming Soon" title={title} subtitle={description} />
    <Card>
      <div className="flex items-center gap-4">
        <Sparkles size={22} style={{ color: P.accent }} />
        <div>
          <div className="text-[14px] font-semibold" style={{ color: "#fff" }}>Phase 2 – rolling out soon</div>
          <div className="text-[12px] mt-1" style={{ color: P.dim }}>This module is planned for the next iteration of the Operations Center.</div>
        </div>
      </div>
    </Card>
  </div>
);

// ------------------------------------------------------------ SEARCH
const useGlobalSearch = (query) => {
  const q = query.trim().toLowerCase();
  if (!q) return { suppliers: [], projects: [], documents: [] };
  return {
    suppliers: SUPPLIERS.filter((s) => (s.name + " " + s.category + " " + s.description + " " + s.tags.join(" ")).toLowerCase().includes(q)),
    projects:  PROJECTS.filter((p) => (p.name + " " + p.desc + " " + p.country).toLowerCase().includes(q)),
    documents: DOCUMENTS.filter((d) => (d.name + " " + d.supplier + " " + d.category).toLowerCase().includes(q)),
  };
};

// ------------------------------------------------------------ ROOT
export default function OpsCenter() {
  const [active, setActive] = React.useState("dashboard");
  const [selectedSupplier, setSelectedSupplier] = React.useState(null);
  const [query, setQuery] = React.useState("");
  const results = useGlobalSearch(query);
  const totalResults = results.suppliers.length + results.projects.length + results.documents.length;

  const goto = React.useCallback((section, sub) => {
    setActive(section);
    if (section === "suppliers" && sub) setSelectedSupplier(sub);
    setQuery("");
  }, []);

  return (
    <div className="min-h-screen flex" style={{ background: P.bg, color: P.ink, fontFamily: "'Inter', 'Poppins', system-ui, sans-serif" }} data-testid="ops-center">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r flex flex-col" style={{ borderColor: P.border, background: "rgba(255,255,255,0.015)" }}>
        <div className="p-5 border-b" style={{ borderColor: P.border }}>
          <Link to="/" className="flex items-center gap-3 group" data-testid="ops-home-link">
            <div className="w-9 h-9 rounded flex items-center justify-center font-bold text-[13px]" style={{ background: `linear-gradient(135deg, ${P.accent} 0%, ${P.accent2} 100%)`, color: P.bg }}>HX</div>
            <div className="leading-none">
              <div className="font-bold text-[13.5px]" style={{ color: "#fff" }}>Operations Center</div>
              <div className="text-[9px] uppercase tracking-[0.28em] mt-1" style={{ color: P.silver }}>BSGOTECH</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {NAV.map((n) => {
            const Icon = n.icon;
            const isActive = active === n.id;
            return (
              <button key={n.id} onClick={() => { setActive(n.id); setSelectedSupplier(null); }}
                data-testid={`nav-${n.id}`}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition text-[13px] font-medium"
                style={{
                  background: isActive ? `${P.accent}18` : "transparent",
                  color: isActive ? P.accent : P.ink,
                  border: `1px solid ${isActive ? `${P.accent}44` : "transparent"}`,
                }}>
                <Icon size={15} />
                <span className="flex-1 text-left">{n.label}</span>
                {n.soon && <span className="text-[8.5px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded" style={{ background: "rgba(255,255,255,0.04)", color: P.dim }}>Soon</span>}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t" style={{ borderColor: P.border }}>
          <Link to="/" className="flex items-center gap-2 px-3 py-2 rounded-md text-[11px] uppercase tracking-[0.2em] hover:bg-white/[0.03] transition" style={{ color: P.silver }} data-testid="ops-back-home">
            <HomeIcon size={12} /> Back to HannoNexus
          </Link>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Top bar with search */}
        <header className="border-b px-6 py-4 flex items-center gap-4" style={{ borderColor: P.border, background: "rgba(255,255,255,0.015)" }}>
          <div className="relative flex-1 max-w-2xl">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: P.silver }} />
            <input
              type="text"
              placeholder="Search suppliers, projects, documents…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="ops-search-input"
              className="w-full pl-10 pr-4 py-2.5 rounded-md text-[13px] outline-none transition"
              style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${P.border}`, color: P.ink }}
              onFocus={(e) => e.target.style.borderColor = P.accent + "88"}
              onBlur={(e) => e.target.style.borderColor = P.border}
            />
            {query && (
              <div className="absolute top-full left-0 right-0 mt-2 rounded-lg overflow-hidden shadow-2xl z-50" style={{ background: P.bg, border: `1px solid ${P.border}` }} data-testid="ops-search-results">
                {totalResults === 0 ? (
                  <div className="p-4 text-[12px]" style={{ color: P.dim }}>No results for &ldquo;{query}&rdquo;</div>
                ) : (
                  <div className="max-h-96 overflow-y-auto">
                    {results.suppliers.length > 0 && <SearchGroup label="Suppliers" items={results.suppliers} onClick={(s) => goto("suppliers", s.id)} render={(s) => s.name + " · " + s.category} />}
                    {results.projects.length > 0 && <SearchGroup label="Projects"  items={results.projects}  onClick={() => goto("projects")}  render={(p) => p.name + " · " + p.country} />}
                    {results.documents.length > 0 && <SearchGroup label="Documents" items={results.documents} onClick={(d) => goto("suppliers", d.supplierId)} render={(d) => d.name + " · " + d.supplier} />}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: P.dim }}>Phase 1 · Shell</div>
        </header>

        <div className="flex-1 p-6 lg:p-8 overflow-y-auto">
          {active === "dashboard"     && <Dashboard     onNavigate={goto} />}
          {active === "suppliers"     && <SuppliersSection selected={selectedSupplier} onSelect={setSelectedSupplier} />}
          {active === "projects"      && <ProjectsSection />}
          {active === "documents"     && <DocumentsSection />}
          {active === "presentations" && <PresentationsSection />}
          {active === "videos"        && <VideosSection />}
          {active === "library"       && <ComingSoon title="Technical Library." description="Curated technical resources: standards (VDE · TÜV · IEC), whitepapers, engineering notes." />}
          {active === "ai"            && <AiAssistantPanel supplierId={selectedSupplier} />}
          {active === "meetings"      && <MeetingsSection />}
          {active === "settings"      && <ComingSoon title="Settings." description="Access control, integrations, notification preferences and workspace customisation." />}
        </div>
      </main>
    </div>
  );
}

const SearchGroup = ({ label, items, onClick, render }) => (
  <div>
    <div className="px-4 py-2 text-[9.5px] uppercase tracking-[0.26em] font-semibold border-t" style={{ color: P.silver, borderColor: P.border, background: "rgba(255,255,255,0.02)" }}>{label} · {items.length}</div>
    {items.map((it, i) => (
      <button key={i} onClick={() => onClick(it)} className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-white/[0.04] transition">
        <ChevronRight size={12} style={{ color: P.accent }} />
        <span className="text-[12.5px]" style={{ color: P.ink }}>{render(it)}</span>
      </button>
    ))}
  </div>
);
